import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionTier } from '@/../generated-client/models';
import { setUserTier } from '@/repository/OrganizationDAO';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { StringKeys, ErrorStringKeys } from '@/resources/strings/StringKeys';
import { buildPoolCounts, hasAvailableSeat } from '@/utils/organization/orgSeatAvailability';
import { getUserAssignedTier } from '@/utils/organization/getUserAssignedTier';
import { parseApiError } from '@/utils/api/parseApiError';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';

interface OrgUserLike {
  auth_id: string;
  first_name: string;
  last_name: string;
}

interface UseSetUserTierOptions {
  updateLocalState?: (
    tier: SubscriptionTier,
    user: OrgUserLike,
    groupName: string,
  ) => void;
  highlightUser?: (authId: string) => void;
}

const POOL_FULL_STRING_KEY: Record<SubscriptionTier, string | undefined> = {
  [SubscriptionTier.Connect]: undefined,
  [SubscriptionTier.Ops]: StringKeys.OPS_SEATS_ARE_FILLED,
  [SubscriptionTier.Respond]: StringKeys.RESPOND_SEATS_ARE_FILLED,
};

const POOL_FULL_ERROR_CODE_TO_STRING_KEY: Record<string, string> = {
  OPS_SEATS_FILLED: StringKeys.OPS_SEATS_ARE_FILLED,
  RESPOND_SEATS_FILLED: StringKeys.RESPOND_SEATS_ARE_FILLED,
};

export const useSetUserTier = (options: UseSetUserTierOptions = {}) => {
  const [inProgress, setInProgress] = useState(false);
  const { organization } = useOrganizationUpdaterProvider();
  const { showErrorToast, showSuccessToast } = useToast();
  const { t } = useTranslation();

  const changeUserTier = async (
    nextTier: SubscriptionTier,
    user: OrgUserLike,
    groupName: string,
  ) => {
    if (inProgress || !organization?.org_code) return;

    const currentTier = getUserAssignedTier(
      Object.values(organization.groups ?? {}),
      user.auth_id,
    );
    if (currentTier === nextTier) return;

    if (
      nextTier !== SubscriptionTier.Connect &&
      !hasAvailableSeat(buildPoolCounts(organization), nextTier)
    ) {
      const poolFullKey = POOL_FULL_STRING_KEY[nextTier];
      if (poolFullKey) showErrorToast(t(poolFullKey));
      return;
    }

    try {
      setInProgress(true);
      await setUserTier(user.auth_id, groupName, nextTier);

      options.updateLocalState?.(nextTier, user, groupName);
      options.highlightUser?.(user.auth_id);

      showSuccessToast(
        t(StringKeys.USER_TIER_UPDATED, {
          user: `${user.first_name} ${user.last_name}`,
          tier: getTierLabelKey(nextTier),
        }),
      );
    } catch (error) {
      const { code } = await parseApiError(error);
      const poolFullKey = code ? POOL_FULL_ERROR_CODE_TO_STRING_KEY[code] : undefined;

      if (poolFullKey) {
        showErrorToast(t(poolFullKey));
      } else {
        showErrorToast(
          t(ErrorStringKeys.FAILED_TO_UPDATE_USERS_TIER, {
            user: `${user.first_name} ${user.last_name}`,
          }),
        );
      }
    } finally {
      setInProgress(false);
    }
  };

  return { changeUserTier, inProgress };
};
